import { computed, ref } from '@vue/composition-api';
import { captureException } from '@sentry/browser';

import { ERRORS_GET_WISH_LIST } from '@/utils/constants/wishListConstants';
import { WISH_LIST_TAB_VALUE } from '@/enums';
import { updateArr } from '@/utils/updateProductsArr';

import useWishListCart from '@/compositions/wishList/useWishListCart';
import useLoading from '@/compositions/useLoading';
import useDigitalDataManager from '@/compositions/ddm/useDigitalDataManager';

const useWishListBasket = () => {
  const { changeListing } = useDigitalDataManager();
  const {
    pagination,
    isEmptyWishList,
    isMayLoadMore,
    getList,
    removeProduct,
    addToWishList,
  } = useWishListCart();
  const { setLoadingFlags, loadingFlags } = useLoading();

  const showSkeleton = ref(true);

  const wishList = ref([]);

  const isLoadingFetchProductsWishList = computed(() => !!(loadingFlags.value?.fetchProductsWishList ?? []).length);

  const changeDdmListing = () => {
    changeListing(wishList.value, {
      currentPage: 1,
      resultCount: pagination.value.total,
    });
  };

  const fetchProductsWishList = async (pageNumber = 1, replace = false) => new Promise((resolve) => {
    setLoadingFlags({ flagName: 'fetchProductsWishList', flag: true });

    const options = {
      page: pageNumber,
      perPage: 20,
      isGetAuthor: true,
      isGetSeries: true,
      isGetProductText: true,
      isGetBadge: true,
      isGetCategory: true,
      isGetProductStats: true,
      isGetProductImage: true,
      isGetProduct: true,
      isGetKitProduct: true,
    };
    getList(options, replace)
      .then((dataRes) => {
        wishList.value = updateArr(wishList.value, wishList.value.concat(dataRes.list), WISH_LIST_TAB_VALUE);
        resolve(dataRes);
      })
      .catch((error) => {
        if (!ERRORS_GET_WISH_LIST.getItemByCode(error?.code)) captureException(error);
      })
      .finally(() => {
        setLoadingFlags({ flagName: 'fetchProductsWishList', flag: false });
        showSkeleton.value = false;
        changeDdmListing();
      });
  });

  const loadMoreWishList = async () => {
    await fetchProductsWishList(pagination.value.currentPage + 1);
  };

  const handlerSpliceWishItem = async ({ productId }) => {
    const index = wishList.value.findIndex(item => item.id === productId);
    if (index !== -1 && wishList.value[index].isDeleted) {
      wishList.value.splice(index, 1);
      isEmptyWishList.value = !wishList.value.length;
    }
  };

  const handlerRestoreWishItem = async ({ productId }) => {
    const index = wishList.value.findIndex(item => item.id === productId);
    if (index !== -1) {
      wishList.value[index].isLoading = true;
      await addToWishList(productId);
      wishList.value[index].isDeleted = false;
      wishList.value[index].isLoading = false;
    }
  };

  const handlerRemoveWishItem = async ({ productId }) => {
    const index = wishList.value.findIndex(item => item.id === productId);
    if (index !== -1) {
      wishList.value[index].isLoading = true;
      await removeProduct(productId);
      wishList.value[index].isDeleted = true;
      wishList.value[index].isLoading = false;
    }
  };

  return {
    wishList,
    wishListTotal: computed(() => pagination.value.total),
    isEmptyWishList,
    isLoadingFetchProductsWishList,
    isMayLoadMore,
    showSkeleton,
    fetchProductsWishList,
    loadMoreWishList,
    handlerSpliceWishItem,
    handlerRestoreWishItem,
    handlerRemoveWishItem,
  };
};

export default useWishListBasket;
